import isEqual from 'lodash.isequal';
import { MutableRefObject, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import logCustomRouteStartingPointAdded from '@alltrails/amplitude/events/logCustomRouteStartingPointAdded';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { addActiveSegmentPath, addSegment, selectCustomRouteActiveSegment, setToastMessage } from '../../../redux/customRoute';
import { transformPointsToServerRoutes } from '../../../redux/customRoutesApi';
import { CustomRouteSegment } from '../../../types/CustomRoute';
import { Coordinates, LatLng } from '../../../types/Geo';
import { isSinglePointSegment } from '../../../utils/customRoutes/validation';
import useRouting from '../useRouting';
import useAdjacentEndpointLinking, { AdjacentEndpointLinkingOptions, SegmentEnd } from './useAdjacentEndpointLinking';
import { getPointForHandle, validateProximityToPreviousPoint } from './validationUtils';

export type PendingHandle = {
  index: number;
  latitude: number;
  longitude: number;
};

// The end of the segment a new point extends, if any: a first point or one before the start handle starts it, one after the end handle or a single point ends it
export const getExtendedEnd = (segment: CustomRouteSegment | undefined, selectedHandleIndex: number | undefined): SegmentEnd | undefined => {
  if (!segment || segment.paths.length === 0) {
    return 'start';
  }
  if (isSinglePointSegment(segment) || selectedHandleIndex === segment.paths.length) {
    return 'end';
  }
  return selectedHandleIndex === 0 ? 'start' : undefined;
};

const useRouteToPoint = (isSmartRoutingRef: MutableRefObject<boolean>, linkOptions?: AdjacentEndpointLinkingOptions) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { activeSegment, selectedHandleIndex } = useAppSelector(state => ({
    activeSegment: selectCustomRouteActiveSegment(state),
    selectedHandleIndex: state.customRoute.selectedHandleIndex
  }));
  const [pendingHandle, setPendingHandle] = useState<PendingHandle | undefined>(undefined);
  const routing = useRouting();
  const { getAdjacentEndpoint, link } = useAdjacentEndpointLinking(linkOptions);

  const routeToPoint = useCallback(
    async ({
      coordinates,
      isPOIAdd,
      isWaypointAdd,
      isOSMPOIAdd,
      closeCutSegment = false
    }: {
      coordinates: Coordinates;
      isPOIAdd?: boolean;
      isWaypointAdd?: boolean;
      isOSMPOIAdd?: boolean;
      closeCutSegment?: boolean;
    }) => {
      const extendedEnd = getExtendedEnd(activeSegment, selectedHandleIndex);
      const isPlainClick = !isPOIAdd && !isWaypointAdd && !isOSMPOIAdd && !closeCutSegment;
      const adjacentEndpoint = extendedEnd && isPlainClick ? getAdjacentEndpoint(extendedEnd, coordinates) : undefined;
      const { lng, lat } = adjacentEndpoint ? { lat: adjacentEndpoint[0], lng: adjacentEndpoint[1] } : coordinates;
      const linkToAdjacentEndpoint = () => extendedEnd && adjacentEndpoint && link(extendedEnd, adjacentEndpoint);

      // If smart routing is enabled verify that the first point is routable, unless it is on an adjacent segment's endpoint and so already on the route
      let startPoint: [number, number] = [lat, lng];
      if (isSmartRoutingRef.current && !adjacentEndpoint && (!activeSegment || activeSegment.paths.length === 0)) {
        const { isSuccess, data } = await routing({
          // Send the first point twice to create a valid route
          routes: transformPointsToServerRoutes([
            [lat, lng],
            [lat, lng]
          ])
        });

        if (!isSuccess) {
          return;
        }

        const serverStartPoint = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0]?.points?.[0];
        if (serverStartPoint) {
          startPoint = serverStartPoint;
        }
      }

      if (!activeSegment) {
        dispatch(addSegment({ paths: [{ points: [startPoint], freeDraw: !isSmartRoutingRef.current }] }));
        linkToAdjacentEndpoint();
        logCustomRouteStartingPointAdded();
        return;
      }

      if (activeSegment.paths.length === 0) {
        dispatch(addActiveSegmentPath({ points: [startPoint], freeDraw: !isSmartRoutingRef.current }));
        linkToAdjacentEndpoint();
        logCustomRouteStartingPointAdded();
        return;
      }

      if (!isSmartRoutingRef.current) {
        const newPoint: [number, number] = [lat, lng];
        const prevPoint = getPointForHandle(activeSegment, selectedHandleIndex);
        if (prevPoint && !validateProximityToPreviousPoint(newPoint, prevPoint)) {
          dispatch(setToastMessage({ iconCode: 'error', message: intl.formatMessage({ defaultMessage: 'Tap or draw closer to your route' }) }));
          return;
        }

        dispatch(addActiveSegmentPath({ points: [[lat, lng]], freeDraw: true }));
        linkToAdjacentEndpoint();
        const actionTaken = isWaypointAdd ? 'waypoint added' : isPOIAdd ? 'add POI to route' : isOSMPOIAdd ? 'add osm poi to route' : 'draw';
        const customRouteMode = isWaypointAdd || isPOIAdd || isOSMPOIAdd ? 'tap' : 'free draw';
        const poiAddLocation = isPOIAdd ? 'custom map screen' : undefined;
        logCustomRouteUpdated({
          custom_route_action_taken: actionTaken,
          custom_route_mode: customRouteMode,
          poi_add_location: poiAddLocation
        });
        return;
      }

      if (selectedHandleIndex === undefined) {
        return;
      }

      let pendingHandleSet = false;
      const isStart = selectedHandleIndex === 0;
      const isEnd = selectedHandleIndex === activeSegment.paths.length;
      // The end handle is at the end of the last path rather than at the beginning of a path
      const pathIndex = isEnd ? selectedHandleIndex - 1 : selectedHandleIndex;
      const path = activeSegment.paths[pathIndex];
      const firstPoint = path.points[0];
      const lastPoint = path.points[path.points.length - 1];

      let points: LatLng[] = [];
      if (isStart && !isSinglePointSegment(activeSegment)) {
        // The new path will start at the click location and end at the first point of the existing path
        points = [[lat, lng], firstPoint];
      } else if (isEnd) {
        // The new path will start at the last point of the existing path and end at the click location
        points = [lastPoint, [lat, lng]];
      } else {
        // The new path will connect the first point of the existing path to the click location
        points = [firstPoint, [lat, lng]];
      }

      const { isSuccess, data } = await routing({
        routes: transformPointsToServerRoutes(points),
        onFetch: () => {
          setPendingHandle({ index: selectedHandleIndex, latitude: lat, longitude: lng });
          pendingHandleSet = true;
        }
      });

      const serverPath = isSuccess && data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0];

      if (serverPath) {
        // add the clicked point to the end of the path if closing a cut segment
        const shouldAppendClickedPoint = closeCutSegment && !isEqual(serverPath.points[serverPath.points.length - 1], [lat, lng]);
        const newPath = shouldAppendClickedPoint ? { ...serverPath, points: [...serverPath.points, [lat, lng] as LatLng] } : serverPath;

        dispatch(addActiveSegmentPath(newPath));
        linkToAdjacentEndpoint();

        const actionTaken = isWaypointAdd ? 'waypoint added' : isPOIAdd ? 'add POI to route' : isOSMPOIAdd ? 'add osm poi to route' : 'add handle';
        logCustomRouteUpdated({
          custom_route_action_taken: actionTaken,
          custom_route_mode: isSmartRoutingRef.current ? 'smart draw' : 'free draw'
        });
      }

      if (pendingHandleSet) {
        setPendingHandle(undefined);
      }
    },
    [activeSegment, selectedHandleIndex, isSmartRoutingRef, dispatch, routing, intl, getAdjacentEndpoint, link]
  );

  return { routeToPoint, pendingHandle };
};

export default useRouteToPoint;
